import { ChevronRight, Loader2, MessageSquare, PlayCircle } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { HumanInputRequiredEvent } from "@/types/api";

interface HumanInputPanelProps {
  request: HumanInputRequiredEvent;
  onResume: (humanResponse: unknown) => void;
  /** True while the Resume request is in flight — disables the submit
   *  to prevent the duplicate-click race the S-021 spike (scenario D)
   *  surfaced on the backend guard. */
  isResuming?: boolean;
}

/**
 * Renders the form the workbench shows when an agent's graph pauses on
 * `interrupt(payload)` (S-021 HITL-3.5).
 *
 * Payload shape is agent-defined. The decisão Q1 do S-021 era "tipado
 * via InputFieldType", mas o dummy agent de teste e a primeira iteração
 * do Functional US-07 vão emitir payloads ad-hoc. Esta versão v1 mostra
 * o payload em JSON e aceita a resposta livre via textarea — basta pra
 * destravar a UX da pausa. Quando os primeiros agentes reais (HITL-4)
 * convergirem num schema, evoluímos pra um `AgentInputForm` real.
 */
export function HumanInputPanel({
  request,
  onResume,
  isResuming = false,
}: HumanInputPanelProps) {
  const { t } = useTranslation();
  const [responseText, setResponseText] = useState("");

  // Payload é ad-hoc por agente. Mostramos title/description (quando existem)
  // como contexto legível sempre visível, e escondemos o JSON cru atrás de um
  // disclosure colapsado por default — payloads grandes (ex: 136 usuários
  // ambíguos) tornam a revisão ruim quando despejados inteiros.
  const payload = (request.payload ?? {}) as Record<string, unknown>;
  const payloadTitle = typeof payload.title === "string" ? payload.title : null;
  const payloadDescription =
    typeof payload.description === "string" ? payload.description : null;

  const handleSubmit = () => {
    if (isResuming) return;
    const trimmed = responseText.trim();
    // Try to parse as JSON so structured responses (e.g. {"approval": "yes"})
    // round-trip cleanly. Falls back to string for free-form replies.
    let value: unknown = trimmed;
    if (trimmed && (trimmed.startsWith("{") || trimmed.startsWith("["))) {
      try {
        value = JSON.parse(trimmed);
      } catch {
        value = trimmed;
      }
    }
    onResume(value);
  };

  return (
    <div className="border-2 border-[#ffc83d] bg-[#fffbef] rounded-[10px] p-5 mb-4 shadow-[0_2px_8px_rgba(255,200,61,0.18)]">
      <div className="flex items-start gap-3 mb-4">
        <div className="bg-[#ffc83d] rounded-full p-2 flex-shrink-0">
          <MessageSquare className="w-4 h-4 text-[#08254c]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] text-[#b57a00] tracking-[0.18em] uppercase font-semibold mb-1">
            {t("Human input required · {{node}}", { node: request.node_name })}
          </div>
          <h3 className="font-brand font-bold text-base text-[#08254c] m-0">
            {t("Waiting for your decision to resume")}
          </h3>
        </div>
      </div>

      {payloadTitle && (
        <p className="text-sm font-medium text-[#08254c] mb-1">{payloadTitle}</p>
      )}
      {payloadDescription && (
        <p className="text-[13px] text-[#5b6b82] leading-[1.5] mb-3">
          {payloadDescription}
        </p>
      )}

      <details className="bg-white border border-[#f0e3a8] rounded-[6px] mb-4 group">
        <summary className="font-mono text-[9px] text-[#7b8799] tracking-[0.15em] uppercase font-semibold px-3 py-2 cursor-pointer select-none flex items-center gap-1.5 list-none [&::-webkit-details-marker]:hidden">
          <ChevronRight className="w-3 h-3 transition-transform group-open:rotate-90" />
          <span className="group-open:hidden">{t("View full payload")}</span>
          <span className="hidden group-open:inline">{t("Hide payload")}</span>
        </summary>
        <pre className="font-mono text-[11px] text-[#08254c] m-0 px-3 pb-3 whitespace-pre-wrap break-words leading-[1.5] max-h-72 overflow-auto">
          {JSON.stringify(request.payload, null, 2)}
        </pre>
      </details>

      <label
        htmlFor="hitl-response"
        className="block font-mono text-[9px] text-[#7b8799] tracking-[0.15em] uppercase font-semibold mb-1.5"
      >
        {t("Your response")}
      </label>
      <textarea
        id="hitl-response"
        value={responseText}
        onChange={(e) => setResponseText(e.target.value)}
        disabled={isResuming}
        rows={3}
        placeholder={t('Free text or JSON (e.g. {"approval": "yes"})')}
        className={cn(
          "w-full border border-[#e8ebf0] rounded-[6px] px-3 py-2",
          "text-sm font-mono text-[#08254c] placeholder:text-[#a8b3c4]",
          "focus:outline-none focus:ring-2 focus:ring-[#ffc83d] focus:border-[#ffc83d]",
          "disabled:bg-[#f7f7f7] disabled:cursor-not-allowed",
          "resize-vertical",
        )}
      />

      <div className="flex justify-end mt-3">
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={isResuming}
          aria-busy={isResuming}
          className="bg-[#ffc83d] hover:bg-[#e8b330] text-[#08254c] font-semibold gap-2"
        >
          {isResuming ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {t("Resuming…")}
            </>
          ) : (
            <>
              <PlayCircle className="w-4 h-4" />
              {t("Resume run")}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
